import React, { useState, useEffect } from 'react';
import { Typography, Box, Grid, Button, Paper, TextField, InputAdornment } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ItemMarquee from '../components/common/ItemMarquee';
import SearchIcon from '@mui/icons-material/Search';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ListAltIcon from '@mui/icons-material/ListAlt';

const ActionCard = ({ title, icon, onClick, color, description }) => (
    <Grid item xs={12} sm={6}>
        <Paper 
            elevation={3} 
            className="action-card"
            onClick={onClick}
        >
            <Box sx={{ color: color, mb: 1 }}>
                {icon}
            </Box>
            <Typography variant="h6" component="div" fontWeight="bold">
                {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                {description}
            </Typography>
        </Paper>
    </Grid>
);

function HomePage() {
  const navigate = useNavigate();
  const [recentItems, setRecentItems] = useState({ found: [], lost: [] });
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchRecentItems = async () => {
        try {
            const { data } = await axios.get('/api/items/recent');
            setRecentItems(data);
        } catch (error) {
            console.error("Failed to fetch recent items:", error);
        }
    };
    fetchRecentItems();
  }, []);

  const handleSearchSubmit = (e) => {
      e.preventDefault();
      if (searchQuery.trim()) {
          navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      }
  };

  return (
    <Box>
        {/* Hero Section */}
        <Box className="hero-section-telugu">
            <Typography variant="h5" component="h1" fontWeight="bold" gutterBottom color="white">
                ఏదైనా పోగొట్టుకున్నారా? దొరికిందా?
            </Typography>
            <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.9)', maxWidth: '700px', margin: 'auto', mb: 4 }}>
                మీరు పోగొట్టుకున్న వస్తువులను కనుగొనడానికి మరియు దొరికిన వస్తువులను తిరిగి ఇవ్వడానికి మా సంఘంతో కనెక్ట్ అవ్వండి.
            </Typography>
            <Paper 
                component="form" 
                onSubmit={handleSearchSubmit}
                sx={{ p: '4px 8px', display: 'flex', alignItems: 'center', maxWidth: 600, margin: 'auto', borderRadius: '50px' }}
            >
                <TextField
                    sx={{ ml: 1, flex: 1, '& fieldset': { border: 'none' } }}
                    placeholder="మీరు పోగొట్టుకున్న వస్తువు కోసం ఇక్కడ వెతకండి..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                    }}
                />
                <Button type="submit" variant="contained" sx={{ borderRadius: '50px', px: 4 }}>
                    వెతకండి
                </Button>
            </Paper>
        </Box>

        {/* Recently Found Items Marquee */}
        <Box sx={{ my: 5 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">ఇటీవల దొరికిన వస్తువులు</Typography>
            <ItemMarquee items={recentItems.found} direction="left-to-right" />
        </Box>

        {/* Recently Lost Items Marquee */}
        <Box sx={{ my: 5 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">ఇటీవల పోయిన వస్తువులు</Typography>
            <ItemMarquee items={recentItems.lost} direction="right-to-left" />
        </Box>

        {/* Action Cards */}
        <Box sx={{ my: 5 }}>
             <Typography variant="h4" gutterBottom fontWeight="bold" textAlign="center">మీరు ఏమి చేయాలనుకుంటున్నారు?</Typography>
             <Grid container spacing={3} mt={2}>
                <ActionCard 
                    title="దొరికిన వస్తువును పోస్ట్ చేయండి" 
                    description="మీకు దొరికిన వస్తువు వివరాలను నమోదు చేసి, దాని నిజమైన యజమానిని కనుగొనడంలో సహాయపడండి."
                    icon={<FindInPageIcon className="action-card-icon" />} 
                    onClick={() => navigate('/found')}
                    color="success.main"
                />
                <ActionCard 
                    title="పోయిన వస్తువును రిపోర్ట్ చేయండి" 
                    description="మీరు పోగొట్టుకున్న వస్తువు వివరాలను నమోదు చేయండి. ఎవరైనా కనుగొంటే, మీకు తెలియజేస్తాము."
                    icon={<ReportProblemIcon className="action-card-icon" />} 
                    onClick={() => navigate('/lost')}
                    color="primary.main"
                />
                <ActionCard 
                    title="నా వస్తువులు" 
                    description="మీరు పోస్ట్ చేసిన దొరికిన మరియు పోయిన వస్తువుల జాబితాను చూడండి మరియు నిర్వహించండి."
                    icon={<ListAltIcon className="action-card-icon" />} 
                    onClick={() => navigate('/my-items-menu')}
                    color="info.main"
                />
                <ActionCard 
                    title="వస్తువుల కోసం వెతకండి" 
                    description="ఇతరులు పోస్ట్ చేసిన దొరికిన వస్తువుల జాబితాను బ్రౌజ్ చేయండి మరియు మీ వస్తువు కోసం వెతకండి."
                    icon={<SearchIcon className="action-card-icon" />} 
                    onClick={() => navigate('/search')}
                    color="secondary.main"
                />
            </Grid>
        </Box>
    </Box>
  );
}

export default HomePage;
